import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    useGetSkillFrameworkByIdQuery,
    usePublishSkillFrameworkMutation,
    useDuplicateSkillFrameworkMutation,
    useDeleteSkillFrameworkMutation,
    useGetOrgSkillFrameworkTypesQuery,
    useGetPublicSkillFrameworkTypesQuery,
} from '@/state/skills/skills-api';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SkillFrameworkSunburst from './components/skill-framework-sunburst';
import SkillFrameworkTree from './components/skill-framework-tree';
import type {
    SkillNode,
    SkillTreeNodeData,
} from './components/skill-framework-chart-utils';
import { transformDataForTree } from './components/skill-framework-chart-utils';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import SkillFrameworkHierarchyDrawer from './components/skill-framework-hierarchy-drawer';
import SkillFrameworkHierarchySheet from './components/skill-framework-hierarchy-sheet';
import CreateSkillFrameworkForm from './components/create-skill-framework-form';
import EditSkillFrameworkForm from './components/edit-skill-framework-form';
import { toast } from 'sonner';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { getMessageByStatus } from '@/lib/error-message';
import { useTenant } from '@/hooks/use-tenant';

const SkillFrameworkDetails = () => {
    const { id } = useParams<{ tenant: string; id: string }>();
    const { tenant } = useTenant();
    const navigate = useNavigate();
    const [view, setView] = useState<'sunburst' | 'tree'>('sunburst');
    const [isDrawerOpen, setIsDrawerOpen] = useState(true);
    const [collapsedMap, setCollapsedMap] = useState<Record<string, boolean>>({});
    const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createParentId, setCreateParentId] = useState<string | number | null>(
        null
    );
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editNodeId, setEditNodeId] = useState<string | null>(null);
    const [editNodeName, setEditNodeName] = useState('');
    const [editNodeDescription, setEditNodeDescription] = useState('');
    const [editNodeSkillTypeId, setEditNodeSkillTypeId] = useState<number | null>(
        null
    );
    const [editParentId, setEditParentId] = useState<string | null>(null);
    const [editParentOptions, setEditParentOptions] = useState<
        { id: string; name: string }[]
    >([]);
    const chartHeight = 'calc(100vh - 190px)';
    const { isSchoolAdmin, isTeacher, isStudent, userRoles } =
        useClientPermissions();
    const isSuperAdmin = userRoles.includes('SUPER ADMIN');
    const canManage = isSchoolAdmin || isTeacher;
    const [publishSkillFramework, { isLoading: isPublishing }] =
        usePublishSkillFrameworkMutation();
    const [duplicateSkillFramework, { isLoading: isDuplicating }] =
        useDuplicateSkillFrameworkMutation();
    const [deleteSkillFramework, { isLoading: isDeleting }] =
        useDeleteSkillFrameworkMutation();

    const {
        data: framework,
        isLoading,
        error,
        refetch: refetchFramework,
    } = useGetSkillFrameworkByIdQuery({
        tenant: tenant || '',
        id: id || '',
    });

    const { data: orgSkillTypes = [] } = useGetOrgSkillFrameworkTypesQuery(
        { tenant: tenant || '' },
        { skip: !tenant || isSuperAdmin }
    );
    const { data: publicSkillTypes = [] } = useGetPublicSkillFrameworkTypesQuery(
        { tenant: tenant || '' },
        { skip: !tenant || !isSuperAdmin }
    );
    const skillTypes = isSuperAdmin ? publicSkillTypes : orgSkillTypes;
    const skillTypeMap = useMemo(() => {
        const map = new Map<string, string>();
        skillTypes.forEach((type) => {
            map.set(String(type.id), type.name);
        });
        return map;
    }, [skillTypes]);
    const getSkillTypeLabel = (id?: number | null) => {
        if (id === null || typeof id === 'undefined') return 'Other';
        return skillTypeMap.get(String(id)) ?? 'Skill/Competence';
    };

    const treeData = useMemo(
        () => (framework ? transformDataForTree(framework as SkillNode) : null),
        [framework]
    );

    const findNodeById = (
        node: SkillTreeNodeData | null,
        nodeId: string
    ): SkillTreeNodeData | null => {
        if (!node) return null;
        if (String(node.id) === nodeId) return node;
        if (node.children) {
            for (const child of node.children) {
                const found = findNodeById(child, nodeId);
                if (found) return found;
            }
        }
        return null;
    };

    const buildTreeIndex = (
        node: SkillTreeNodeData,
        parentId: string | null,
        acc: {
            nodes: { id: string; name: string }[];
            parentMap: Map<string, string | null>;
        }
    ) => {
        acc.nodes.push({ id: node.id, name: node.name });
        acc.parentMap.set(node.id, parentId);
        node.children?.forEach((child) => buildTreeIndex(child, node.id, acc));
    };

    const collectDescendantIds = (
        node: SkillTreeNodeData,
        acc: Set<string>
    ) => {
        node.children?.forEach((child) => {
            acc.add(child.id);
            collectDescendantIds(child, acc);
        });
    };

    const nodeStack = (() => {
        if (!treeData) return [];
        if (selectedNodeIds.length === 0) return [treeData];
        const resolved = selectedNodeIds
            .map((nodeId) => findNodeById(treeData, nodeId))
            .filter((node): node is SkillTreeNodeData => !!node);
        return resolved.length ? resolved : [treeData];
    })();

    const sheetStack = selectedNodeIds.length ? selectedNodeIds : [];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center text-red-500">
                Error loading framework details. Please try again later.
            </div>
        );
    }

    if (!framework) {
        return (
            <div className="p-8 text-center text-muted-foreground">
                Framework not found.
            </div>
        );
    }

    const typedFramework = framework as SkillNode;
    const isPublished = !!typedFramework.published;
    const rootId = typedFramework.id;
    const tenantCode = tenant || '';

    const handleError = (err: unknown, fallback: string) => {
        const error = err as ApiErrorResponse;
        toast.error(fallback, {
            description: getMessageByStatus(error?.status || 500),
        });
    };

    return (
        <div className="p-4 md:p-6 space-y-4 w-full max-w-screen-2xl mx-auto">
            <Button
                variant="ghost"
                className="gap-2 pl-0 hover:bg-transparent hover:text-primary"
                onClick={() => navigate(-1)}
            >
                <ArrowLeft className="h-4 w-4" />
                Back to Frameworks
            </Button>

            <div className="mt-4">
                <div className="relative">
                    <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="absolute top-2 right-2 z-40 h-7 w-7"
                        onClick={() => setIsDrawerOpen((prev) => !prev)}
                    >
                        {isDrawerOpen ? (
                            <ChevronsRight className="h-4 w-4" />
                        ) : (
                            <ChevronsLeft className="h-4 w-4" />
                        )}
                    </Button>

                    <div
                        className={`grid gap-0 transition-all ${
                            isDrawerOpen
                                ? 'grid-cols-[minmax(0,1fr)_360px]'
                                : 'grid-cols-1'
                        }`}
                    >
                        <div className="relative min-w-0 overflow-hidden">
                            <Tabs
                                value={view}
                                onValueChange={(value) =>
                                    setView(value as 'sunburst' | 'tree')
                                }
                                className="space-y-0"
                            >
                                <div className="absolute top-3 left-3 z-20">
                                    <TabsList>
                                        <TabsTrigger value="sunburst">Sunburst</TabsTrigger>
                                        <TabsTrigger value="tree">Tree</TabsTrigger>
                                    </TabsList>
                                </div>

                                <TabsContent value="sunburst" className="mt-0">
                                    <SkillFrameworkSunburst
                                        framework={typedFramework}
                                        height={chartHeight}
                                    />
                                </TabsContent>

                                <TabsContent value="tree" className="mt-0">
                                    {view === 'tree' && (
                                        <SkillFrameworkTree
                                            framework={typedFramework}
                                            height={chartHeight}
                                        />
                                    )}
                                </TabsContent>
                            </Tabs>
                        </div>

                        {isDrawerOpen && (
                            <SkillFrameworkHierarchyDrawer
                                treeData={treeData}
                                nodeStack={nodeStack}
                                getSkillTypeLabel={getSkillTypeLabel}
                                collapsedMap={collapsedMap}
                                onToggle={(nodeId) =>
                                    setCollapsedMap((prev) => ({
                                        ...prev,
                                        [nodeId]: !prev[nodeId],
                                    }))
                                }
                                onView={(nodeId) => {
                                    if (!treeData) return;
                                    setSelectedNodeIds([String(nodeId)]);
                                    if (!isDrawerOpen) setIsDrawerOpen(true);
                                }}
                                isStudent={isStudent}
                                canManage={canManage}
                                height={chartHeight}
                                isPublished={isPublished}
                                isPublishing={isPublishing}
                                onPublish={() => {
                                    if (!id || isPublished || isPublishing) return;
                                    void (async () => {
                                        try {
                                            await publishSkillFramework({
                                                tenant: tenantCode,
                                                id,
                                                published: true,
                                            }).unwrap();
                                            toast.success('Framework published');
                                        } catch (err) {
                                            handleError(err, 'Publish failed');
                                        }
                                    })();
                                }}
                                onCreateChild={(nodeId) => {
                                    setCreateParentId(nodeId);
                                    setIsCreateModalOpen(true);
                                }}
                                onEdit={(nodeId) => {
                                    if (!treeData) return;
                                    const target = findNodeById(treeData, String(nodeId));
                                    if (!target) return;
                                    const index = {
                                        nodes: [] as { id: string; name: string }[],
                                        parentMap: new Map<string, string | null>(),
                                    };
                                    buildTreeIndex(treeData, null, index);
                                    const descendants = new Set<string>();
                                    collectDescendantIds(target, descendants);
                                    const options = index.nodes.filter(
                                        (item) =>
                                            item.id !== target.id &&
                                            !descendants.has(item.id)
                                    );
                                    setEditParentOptions(options);
                                    setEditNodeId(target.id);
                                    setEditNodeName(target.name);
                                    setEditNodeDescription(target.description || '');
                                    setEditNodeSkillTypeId(target.skillTypeId ?? null);
                                    setEditParentId(
                                        index.parentMap.get(target.id) ?? null
                                    );
                                    setIsEditModalOpen(true);
                                }}
                                isDuplicating={isDuplicating}
                                onDuplicate={(nodeId) => {
                                    if (isDuplicating || isDeleting) return;
                                    void (async () => {
                                        try {
                                            await duplicateSkillFramework({
                                                tenant: tenantCode,
                                                id: nodeId,
                                                rootId,
                                            }).unwrap();
                                            toast.success('Skill duplicated');
                                        } catch (err) {
                                            handleError(err, 'Duplicate failed');
                                        }
                                    })();
                                }}
                                isDeleting={isDeleting}
                                onDelete={async (nodeId) => {
                                    if (isDeleting) return;
                                    try {
                                        await deleteSkillFramework({
                                            tenant: tenantCode,
                                            id: nodeId,
                                            rootId,
                                        }).unwrap();
                                        toast.success('Skill deleted');
                                        if (String(nodeId) === String(rootId)) {
                                            navigate(
                                                `/${tenantCode}/admin-tools/skills-and-competences`
                                            );
                                        }
                                    } catch (err) {
                                        handleError(err, 'Delete failed');
                                    }
                                }}
                                rootId={rootId}
                            />
                        )}
                    </div>
                </div>
            </div>

            {sheetStack.map((nodeId, index) => {
                if (!treeData) return null;
                const node = findNodeById(treeData, nodeId);
                if (!node) return null;
                return (
                    <SkillFrameworkHierarchySheet
                        key={`${node.id}-${index}`}
                        isOpen
                        depth={index}
                        node={node}
                        getSkillTypeLabel={getSkillTypeLabel}
                        isStudent={isStudent}
                        canManage={canManage}
                        isPublished={isPublished}
                        isPublishing={isPublishing}
                        onPublish={() => {
                            if (!id || isPublished || isPublishing) return;
                            void (async () => {
                                try {
                                    await publishSkillFramework({
                                        tenant: tenantCode,
                                        id,
                                        published: true,
                                    }).unwrap();
                                    toast.success('Framework published');
                                } catch (err) {
                                    handleError(err, 'Publish failed');
                                }
                            })();
                        }}
                        isDuplicating={isDuplicating}
                        onDuplicate={(duplicateId) => {
                            if (isDuplicating || isDeleting) return;
                            void (async () => {
                                try {
                                    await duplicateSkillFramework({
                                        tenant: tenantCode,
                                        id: duplicateId,
                                        rootId,
                                    }).unwrap();
                                    toast.success('Skill duplicated');
                                } catch (err) {
                                    handleError(err, 'Duplicate failed');
                                }
                            })();
                        }}
                        isDeleting={isDeleting}
                        onDelete={async (deleteId) => {
                            if (isDeleting) return;
                            try {
                                await deleteSkillFramework({
                                    tenant: tenantCode,
                                    id: deleteId,
                                    rootId,
                                }).unwrap();
                                toast.success('Skill deleted');
                                if (String(deleteId) === String(rootId)) {
                                    navigate(
                                        `/${tenantCode}/admin-tools/skills-and-competences`
                                    );
                                }
                            } catch (err) {
                                handleError(err, 'Delete failed');
                            }
                        }}
                        rootId={rootId}
                        onCreateChild={(nodeId) => {
                            setCreateParentId(nodeId);
                            setIsCreateModalOpen(true);
                        }}
                        onEdit={(nodeId) => {
                            if (!treeData) return;
                            const target = findNodeById(treeData, String(nodeId));
                            if (!target) return;
                            const index = {
                                nodes: [] as { id: string; name: string }[],
                                parentMap: new Map<string, string | null>(),
                            };
                            buildTreeIndex(treeData, null, index);
                            const descendants = new Set<string>();
                            collectDescendantIds(target, descendants);
                            const options = index.nodes.filter(
                                (item) =>
                                    item.id !== target.id &&
                                    !descendants.has(item.id)
                            );
                            setEditParentOptions(options);
                            setEditNodeId(target.id);
                            setEditNodeName(target.name);
                            setEditNodeDescription(target.description || '');
                            setEditNodeSkillTypeId(target.skillTypeId ?? null);
                            setEditParentId(index.parentMap.get(target.id) ?? null);
                            setIsEditModalOpen(true);
                        }}
                        onClose={() =>
                            setSelectedNodeIds((prev) => prev.slice(0, index))
                        }
                        onView={(childId) =>
                            setSelectedNodeIds((prev) => {
                                const nextId = String(childId);
                                const base = prev.slice(0, index + 1);
                                if (base[base.length - 1] === nextId) return base;
                                return [...base, nextId];
                            })
                        }
                    />
                );
            })}
            <CreateSkillFrameworkForm
                tenant={tenantCode}
                parentId={createParentId}
                isOpen={isCreateModalOpen}
                onClose={() => {
                    setIsCreateModalOpen(false);
                    setCreateParentId(null);
                }}
                variant="skill"
                onCreated={() => {
                    void refetchFramework();
                }}
            />
            {editNodeId && (
                <EditSkillFrameworkForm
                    tenant={tenantCode}
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setEditNodeId(null);
                    }}
                    nodeId={editNodeId}
                    rootId={rootId}
                    nodeName={editNodeName}
                    nodeDescription={editNodeDescription}
                    nodeSkillTypeId={editNodeSkillTypeId}
                    currentParentId={editParentId}
                    parentOptions={editParentOptions}
                    isRoot={String(editNodeId) === String(rootId)}
                    onUpdated={() => {
                        void refetchFramework();
                    }}
                />
            )}
        </div>
    );
};

export default SkillFrameworkDetails;
